import axios from 'axios';
import { InstagramDirectAuthResponse, InstagramDirectProfile, InstagramPage, InstagramPostData } from '../types/instagram.types';
import { getMetaClientId } from '../../../config/metaEnv';
import { debug } from "../../../../../utils/debugLogger";
import { createOAuthState } from "../../../../../utils/oauthState";
import { exchangeOAuthCode } from "../../../../../services/oauthTokenExchange";

interface InstagramAuthResponse {
  access_token: string;
  email: string;
  pages: InstagramPage[];
}

export const initiateInstagramAuth = () => {
  const appId = getMetaClientId();
  const redirectUri = process.env.REACT_APP_INSTAGRAM_REDIRECT_URI || `${window.location.origin}/connect/instagram/callback`;

  const scope =
    "instagram_basic,instagram_content_publish,pages_show_list,pages_read_engagement,instagram_manage_insights,business_management";
  const params = new URLSearchParams({
    client_id: appId,
    redirect_uri: redirectUri,
    scope,
    response_type: "code",
  });

  // Build the Facebook OAuth URL (which handles Instagram Business authentication)
  const authUrl = `https://www.facebook.com/v18.0/dialog/oauth?${params.toString()}`;
  
  debug.log("Initiating Instagram authentication:", {
    clientId: appId ? "Available" : "Missing",
    redirectUri,
    scope
  });
  
  // Redirect the user to the authentication URL
  window.location.href = authUrl;
};

export const initiateInstagramDirectAuth = () => {
  const appId = getMetaClientId();
  const redirectUri = process.env.REACT_APP_INSTAGRAM_DIRECT_REDIRECT_URI || 
    `${window.location.origin}/connect/instagram-direct/callback`;
  
  // Encode the redirect URI
  const encodedRedirectUri = encodeURIComponent(redirectUri);
  
  // Required permissions for Instagram Direct
  const scope = encodeURIComponent('user_profile,user_media');
  
  // Build the Instagram OAuth URL
  const state = createOAuthState("instagram_direct");
  const authUrl = `https://api.instagram.com/oauth/authorize?client_id=${appId}&redirect_uri=${encodedRedirectUri}&scope=${scope}&response_type=code&state=${encodeURIComponent(state)}`;
  
  debug.log("Initiating Instagram Direct authentication:", {
    clientId: appId ? "Available" : "Missing",
    redirectUri,
    scope
  });
  
  // Redirect the user to the authentication URL
  window.location.href = authUrl;
};



export const handleInstagramCallback = async (code: string): Promise<InstagramAuthResponse> => {
  if (!code) {
    throw new Error('Authorization code is required');
  }

  try {
    const redirectUri =
      process.env.REACT_APP_INSTAGRAM_REDIRECT_URI ||
      `${window.location.origin}/connect/instagram/callback`;
    // Exchanged on the server: the app secret never reaches the browser.
    const tokenData = await exchangeOAuthCode<{ access_token: string }>("meta", code, redirectUri);
    const access_token = tokenData.access_token;

    // Step 2: Fetch user profile (id, name, email)
    let email = '';
    try {
      const userResponse = await axios.get('https://graph.facebook.com/me', {
        params: {
          fields: 'id,name,email',
          access_token,
        }
      });
      email = userResponse.data.email || '';
    } catch (profileError) {
      debug.warn('Could not fetch Instagram user email:', profileError);
    }

    // Step 3: Get Instagram business accounts
    const accountsResponse = await axios.get('https://graph.facebook.com/v18.0/me/accounts', {
      params: {
        access_token,
        fields: 'name,access_token,instagram_business_account{id,name},business,category',
      }
    });

    // Filter pages with Instagram business accounts
    const pages = accountsResponse.data.data.filter(
      (page: any) => page.instagram_business_account
    );

    return {
      access_token,
      email,
      pages,
    };
  } catch (error: any) {
    debug.error('Instagram API Error:', {
      message: error.message,
      response: error.response?.data,
      config: {
        url: error.config?.url,
        params: error.config?.params,
      }
    });
    throw error;
  }
};

export const handleInstagramDirectCallback = async (code: string): Promise<InstagramDirectAuthResponse & { profile: InstagramDirectProfile }> => {
  if (!code) {
    throw new Error('Authorization code is required');
  }

  try {
    const redirectUri =
      process.env.REACT_APP_INSTAGRAM_DIRECT_REDIRECT_URI ||
      `${window.location.origin}/connect/instagram-direct/callback`;
    // Exchanged on the server: the app secret never reaches the browser.
    const { access_token, user_id } = await exchangeOAuthCode<{ access_token: string; user_id: string }>(
      "instagram", code, redirectUri);

    // Step 2: Fetch user profile
    const profileResponse = await axios.get(`https://graph.instagram.com/me`, {
      params: {
        fields: 'id,username,account_type,media_count',
        access_token
      }
    });

    return {
      access_token,
      user_id,
      profile: profileResponse.data
    };
  } catch (error: any) {
    debug.error('Instagram Direct API Error:', {
      message: error.message,
      response: error.response?.data,
      config: {
        url: error.config?.url,
        params: error.config?.params,
      }
    });
    throw error;
  }
};

export const testInstagramPost = async ({
  accountId,
  accessToken,
  imageUrl,
  caption,
}: InstagramPostData): Promise<any> => {
  try {
    // Step 1: Create a media container
    debug.log("Step 1: Creating media container...");
    const containerResponse = await axios.post(
      `https://graph.facebook.com/v18.0/${accountId}/media`,
      {
        access_token: accessToken,
        image_url: imageUrl,
        caption: caption,
        media_type: "IMAGE"
      }
    );

    if (!containerResponse.data.id) {
      throw new Error("Failed to create media container");
    }

    debug.log("Media container created:", containerResponse.data);
    const creationId = containerResponse.data.id;

    // Step 2: Wait for media processing
    debug.log("Step 2: Waiting for media processing...");
    await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds

    // Step 3: Publish the content
    debug.log("Step 3: Publishing content...");
    const publishResponse = await axios.post(
      `https://graph.facebook.com/v18.0/${accountId}/media_publish`,
      {
        access_token: accessToken,
        creation_id: creationId
      }
    );

    debug.log("Post published successfully:", publishResponse.data);
    return publishResponse.data;

  } catch (error: any) {
    debug.error("Instagram API Error:", {
      error: error.response?.data?.error || error.message,
      status: error.response?.status,
      endpoint: error.config?.url,
      params: error.config?.data
    });
    throw error;
  }
};